import clsx from "clsx";
import { FC, useMemo } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import {
  Loader,
  SortDir,
  SortableTableHeader,
  TableFrame,
  TablePagination,
  compareNullableBigInts,
  compareStrings,
  useIsTouchDevice,
  useTablePagination,
  useTableSort,
  useTranslation,
} from "rujira.ui";
import { usePreloadedAccountData } from "../../services/accountData";
import {
  BalancesPoolFragment$data,
  BalancesPoolFragment$key,
} from "./__generated__/BalancesPoolFragment.graphql";
import { BowPoolXykRow, BowPoolXykThumb } from "./BowPoolXykThumb";
import { GhostVaultRow, GhostVaultThumb } from "./GhostVaultThumb";
import { IndexVaultPortfolioRow } from "./IndexVaultPortfolioRow";
import { IndexVaultThumb } from "./IndexVaultThumb";
import { StakingPoolPortfolioRow } from "./StakingPoolPortfolioRow";
import { StakingPoolThumb } from "./StakingPoolThumb";
import { ThorchainPoolRow, ThorchainPoolThumb } from "./ThorchainPoolThumb";

const STRATEGY_COLUMNS = [
  { id: "logo", width: "3rem" },
  { id: "strategy", width: "calc(20% - 3rem)", minWidth: "8rem" },
  { id: "type", width: "18%", minWidth: "8rem" },
  { id: "assets", width: "24%", minWidth: "15rem" },
  { id: "apr", width: "14%", minWidth: "5rem" },
  { id: "value", width: "10%", minWidth: "6rem" },
  { id: "share", width: "7%", minWidth: "3rem" },
  { id: "manage", width: "7%", minWidth: "6rem" },
] as const;

const STRATEGY_SORT_KEYS = [
  "strategy",
  "type",
  "assets",
  "apr",
  "value",
] as const;
type StrategySortKey = (typeof STRATEGY_SORT_KEYS)[number];

type StrategyItem =
  NonNullable<BalancesPoolFragment$data>["strategies"][number];

type PreparedStrategy = {
  item: StrategyItem & { readonly id: string };
  strategy: string;
  type: string;
  assets: string;
  apr: bigint | null;
  value: bigint;
};

const prepareStrategy = (item: StrategyItem): PreparedStrategy | null => {
  if (!("id" in item)) return null;

  switch (item.__typename) {
    case "ThorchainLiquidityProvider": {
      const symbol = item.asset.metadata.symbol;
      return {
        item,
        strategy: `${symbol}/RUNE`,
        type: "AMM - THORChain CLP",
        assets: `${symbol}/RUNE`,
        apr: null,
        value: BigInt(item.valueUsd),
      };
    }
    case "BowAccount": {
      const x = item.pool.config.x?.metadata.symbol || "";
      const y = item.pool.config.y?.metadata.symbol || "";
      return {
        item,
        strategy: `${x}/${y}`,
        type: "AMM - XYK",
        assets: `${x}/${y}`,
        apr: null,
        value: BigInt(item.valueUsd),
      };
    }
    case "IndexAccount": {
      const symbol = item.index.shareAsset.metadata.symbol || "";
      return {
        item,
        strategy: symbol,
        type: "Index",
        assets: item.allocations
          .map((allocation) => allocation.asset.metadata.symbol)
          .join("/"),
        apr:
          item.index.status.apr.value == null
            ? null
            : BigInt(item.index.status.apr.value),
        value: BigInt(item.sharesValue),
      };
    }
    case "StakingAccount": {
      const symbol = item.pool.bondAsset.metadata.symbol || "";
      return {
        item,
        strategy: symbol,
        type: "Staking",
        assets: `${symbol}/${item.pendingRevenue.asset.metadata.symbol}`,
        apr:
          item.pool.summary.apr.value == null
            ? null
            : BigInt(item.pool.summary.apr.value),
        value: BigInt(item.valueUsd),
      };
    }
    case "GhostVaultAccount": {
      const symbol = item.vault.asset.metadata.symbol || "";
      return {
        item,
        strategy: symbol,
        type: "Lending",
        assets: symbol,
        apr:
          item.vault.status.apr.value == null
            ? null
            : BigInt(item.vault.status.apr.value),
        value: BigInt(item.valueUsd),
      };
    }
    default:
      return null;
  }
};

const strategyDefaultSortDir = (key: StrategySortKey) =>
  key === "strategy" || key === "type" || key === "assets"
    ? SortDir.Asc
    : SortDir.Desc;

const compareStrategies = (
  a: PreparedStrategy,
  b: PreparedStrategy,
  sortBy: StrategySortKey,
  sortDir: SortDir
) => {
  switch (sortBy) {
    case "strategy":
      return compareStrings(a.strategy, b.strategy, sortDir);
    case "type":
      return compareStrings(a.type, b.type, sortDir);
    case "assets":
      return compareStrings(a.assets, b.assets, sortDir);
    case "apr":
      return compareNullableBigInts(a.apr, b.apr, sortDir);
    case "value":
      return compareNullableBigInts(a.value, b.value, sortDir);
  }
};

const fragment = graphql`
  fragment BalancesPoolFragment on Account {
    bow {
      id
      ...BalanceThumbDualFragment
    }
    strategies {
      __typename
      ... on ThorchainLiquidityProvider {
        id
        asset {
          asset
          metadata {
            symbol
          }
        }
        valueUsd
        ...ThorchainPoolThumbFragment
      }
      ... on BowAccount {
        id
        pool {
          config {
            ... on BowConfigXyk {
              x {
                metadata {
                  symbol
                }
              }
              y {
                metadata {
                  symbol
                }
              }
            }
          }
        }
        valueUsd
        ...BowPoolXykThumbFragment
      }
      ... on IndexAccount {
        id
        index {
          shareAsset {
            metadata {
              symbol
            }
          }
          status {
            apr {
              value
            }
          }
        }
        sharesValue
        allocations {
          asset {
            metadata {
              symbol
            }
          }
        }
        ...IndexVaultThumbFragment
        ...IndexVaultPortfolioRowFragment
      }
      ... on StakingAccount {
        id
        pool {
          bondAsset {
            metadata {
              symbol
            }
          }
          summary {
            apr {
              value
            }
          }
        }
        pendingRevenue {
          asset {
            metadata {
              symbol
            }
          }
        }
        valueUsd
        ...StakingPoolThumbFragment
        ...StakingPoolPortfolioRowFragment
      }
      ... on GhostVaultAccount {
        id
        vault {
          asset {
            asset
            metadata {
              symbol
            }
          }
          status {
            apr {
              value
            }
          }
        }
        valueUsd
        ...GhostVaultThumbFragment
      }
    }
  }
`;

export const Balances: FC<{ a?: BalancesPoolFragment$key }> = () => {
  const { accountData } = usePreloadedAccountData();
  const { t } = useTranslation("strategies");

  const account = useFragment<BalancesPoolFragment$key>(fragment, accountData);

  return (
    <div className="pools__positions">
      <h3 className="h3 mt-5 mt-lg-12">{t("myPositions")}</h3>
      <div className="pools__grid">
        {account?.strategies.map((x) => {
          return "id" in x ? <Thumb key={x.id} item={x} /> : null;
        })}
        {(!account?.strategies || account?.strategies.length === 0) && (
          <div className="relative card p-3">
            <h4 className="h4 fs-16 lh-19 fw-500 color-grey text-center mb-0">
              {t("noPositionsFound")}
            </h4>
          </div>
        )}
      </div>
    </div>
  );
};

export const BalancesFallback = () => {
  const { t } = useTranslation("strategies");
  return (
    <div className="pools__positions">
      <h3 className="h3 mt-5 mt-lg-12 flex">
        {t("myPositions")} <Loader className=" ml-2 mt-1 w-4 h-4" />
      </h3>
    </div>
  );
};

const Thumb: FC<{
  item: NonNullable<BalancesPoolFragment$data>["strategies"][number];
}> = ({ item }) => {
  switch (item.__typename) {
    case "ThorchainLiquidityProvider":
      return <ThorchainPoolThumb k={item} />;
    case "BowAccount":
      return <BowPoolXykThumb k={item} />;
    case "IndexAccount":
      return <IndexVaultThumb k={item} />;
    case "StakingAccount":
      return <StakingPoolThumb k={item} />;
    case "GhostVaultAccount":
      return <GhostVaultThumb k={item} />;
  }
};

export const StrategiesPortfolio: FC<{ a?: BalancesPoolFragment$key }> = () => {
  const { accountData } = usePreloadedAccountData();
  const account = useFragment<BalancesPoolFragment$key>(fragment, accountData);
  const isTouchDevice = useIsTouchDevice();
  const { t } = useTranslation("strategies");
  const { sortBy, sortDir, toggleSort } = useTableSort<StrategySortKey>({
    initialBy: "value",
    initialDir: SortDir.Desc,
    defaultDir: strategyDefaultSortDir,
  });
  const strategies = useMemo(
    () =>
      (account?.strategies || [])
        .map(prepareStrategy)
        .filter((entry): entry is PreparedStrategy => entry !== null),
    [account?.strategies]
  );
  const sortedStrategies = useMemo(
    () =>
      [...strategies].sort((a, b) => {
        const comparison = compareStrategies(a, b, sortBy, sortDir);
        return comparison || a.item.id.localeCompare(b.item.id);
      }),
    [sortBy, sortDir, strategies]
  );
  const { pageRows: visibleStrategies, pagination } = useTablePagination(
    sortedStrategies,
    { resetKey: `${sortBy}:${sortDir}` }
  );
  const sortHeaderProps = { active: sortBy, dir: sortDir, onSort: toggleSort };

  return strategies.length ? (
    <>
      <TableFrame
        className="strategy-balances-table"
        columns={STRATEGY_COLUMNS}
        stickyFirstColumn
        tableClassName={clsx("table--big condensed portfolio__balances mb-0", {
          "table--no-hover": isTouchDevice,
        })}>
        <thead>
          <tr>
            <th aria-hidden="true" />
            <SortableTableHeader
              {...sortHeaderProps}
              value="strategy"
              label={t("strategy")}
              style={{ paddingLeft: 0 }}
            />
            <SortableTableHeader
              {...sortHeaderProps}
              value="type"
              label={t("type")}
            />
            <SortableTableHeader
              {...sortHeaderProps}
              value="assets"
              label={t("assets")}
            />
            <SortableTableHeader
              {...sortHeaderProps}
              value="apr"
              label="APR"
              align="center"
            />
            <SortableTableHeader
              {...sortHeaderProps}
              value="value"
              label={t("value")}
              align="right"
            />
            <th aria-label="Share" />
            <th aria-label="Manage" />
          </tr>
        </thead>
        <tbody>
          {visibleStrategies.map(({ item }) => (
            <PortfolioRow key={item.id} item={item} />
          ))}
        </tbody>
      </TableFrame>
      <TablePagination {...pagination} />
    </>
  ) : (
    <div className="fs-16 lh-19 fw-500 color-grey text-center">
      {t("noStrategyBalancesFound")}
    </div>
  );
};

const PortfolioRow: FC<{
  item: NonNullable<BalancesPoolFragment$data>["strategies"][number];
}> = ({ item }) => {
  switch (item.__typename) {
    case "ThorchainLiquidityProvider":
      return <ThorchainPoolRow k={item} />;
    case "BowAccount":
      return <BowPoolXykRow k={item} />;
    case "IndexAccount":
      return <IndexVaultPortfolioRow k={item} />;
    case "StakingAccount":
      return <StakingPoolPortfolioRow k={item} />;
    case "GhostVaultAccount":
      return <GhostVaultRow k={item} />;
  }
};
